import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../services/apiClient';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';

export const getUsers = createAsyncThunk(
    'users/getUsers',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching users with params:', params);

            const response = await apiClient.get(API_ENDPOINTS.USERS, { params });
            console.log('Users response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Users retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch users',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get users error:', error);

            let errorMessage = 'Failed to fetch users';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch users';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getUser = createAsyncThunk(
    'users/getUser',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching user:', id);

            const response = await apiClient.get(API_ENDPOINTS.USER_SHOW(id));
            console.log('User response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'User retrieved successfully',
                    user: data?.user || null,
                    permissions: data?.permissions || [],
                    activityLog: data?.activity_log || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch user',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get user error:', error);

            let errorMessage = 'Failed to fetch user';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch user';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createUser = createAsyncThunk(
    'users/createUser',
    async (userData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Creating user with data:', userData);

            const response = await apiClient.post(API_ENDPOINTS.CREATE_USER, userData);
            console.log('Create user response:', response.data);

            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'User created successfully',
                    user: data?.user || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create user',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Create user error:', error);

            let errorMessage = 'Failed to create user';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create user';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateUser = createAsyncThunk(
    'users/updateUser',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Updating user:', id);
            console.log('Update data:', data);

            const response = await apiClient.post(API_ENDPOINTS.UPDATE_USER(id), data);
            console.log('Update user response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'User updated successfully',
                    user: responseData?.user || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update user',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update user error:', error);

            let errorMessage = 'Failed to update user';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update user';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deleteUser = createAsyncThunk(
    'users/deleteUser',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Deleting user:', id);

            const response = await apiClient.delete(API_ENDPOINTS.DELETE_USER(id));
            console.log('Delete user response:', response.data);

            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'User deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete user',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Delete user error:', error);

            let errorMessage = 'Failed to delete user';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete user';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const approveUser = createAsyncThunk(
    'users/approveUser',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Approving user:', id);

            const response = await apiClient.post(API_ENDPOINTS.APPROVE_USER(id));
            console.log('Approve user response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'User approved successfully',
                    user: data?.user || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to approve user',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Approve user error:', error);

            let errorMessage = 'Failed to approve user';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to approve user';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const rejectUser = createAsyncThunk(
    'users/rejectUser',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Rejecting user:', id);

            const response = await apiClient.post(API_ENDPOINTS.REJECT_USER(id));
            console.log('Reject user response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'User rejected successfully',
                    user: data?.user || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to reject user',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Reject user error:', error);

            let errorMessage = 'Failed to reject user';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to reject user';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const activateUser = createAsyncThunk(
    'users/activateUser',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Activating user:', id);

            const response = await apiClient.post(API_ENDPOINTS.ACTIVATE_USER(id));
            console.log('Activate user response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'User activated successfully',
                    user: data?.user || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to activate user',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Activate user error:', error);

            let errorMessage = 'Failed to activate user';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to activate user';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deactivateUser = createAsyncThunk(
    'users/deactivateUser',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Deactivating user:', id);

            const response = await apiClient.post(API_ENDPOINTS.DEACTIVATE_USER(id));
            console.log('Deactivate user response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'User deactivated successfully',
                    user: data?.user || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to deactivate user',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Deactivate user error:', error);

            let errorMessage = 'Failed to deactivate user';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to deactivate user';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const blockUser = createAsyncThunk(
    'users/blockUser',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Blocking user:', id);

            const response = await apiClient.post(API_ENDPOINTS.BLOCK_USER(id));
            console.log('Block user response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'User blocked successfully',
                    user: data?.user || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to block user',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Block user error:', error);

            let errorMessage = 'Failed to block user';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to block user';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const unblockUser = createAsyncThunk(
    'users/unblockUser',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Unblocking user:', id);

            const response = await apiClient.post(API_ENDPOINTS.UNBLOCK_USER(id));
            console.log('Unblock user response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'User unblocked successfully',
                    user: data?.user || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to unblock user',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Unblock user error:', error);

            let errorMessage = 'Failed to unblock user';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to unblock user';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const bulkActionUsers = createAsyncThunk(
    'users/bulkAction',
    async ({ userIds, action, reason = null }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Bulk action:', action, 'on users:', userIds);

            const response = await apiClient.post(API_ENDPOINTS.BULK_USER_ACTION, {
                user_ids: userIds,
                action: action,
                reason: reason
            });
            console.log('Bulk action response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Bulk action completed successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to perform bulk action',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Bulk action error:', error);

            let errorMessage = 'Failed to perform bulk action';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to perform bulk action';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

const initialState = {
    users: [],
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
        stats: {
            total: 0,
            active: 0,
            pending: 0,
            inactive: 0,
            blocked: 0
        }
    },
    currentUser: null,
    currentUserPermissions: [],
    currentUserActivity: [],
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const userSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearUserState: (state) => {
            state.users = [];
            state.pagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0,
                stats: {
                    total: 0,
                    active: 0,
                    pending: 0,
                    inactive: 0,
                    blocked: 0
                }
            };
            state.currentUser = null;
            state.currentUserPermissions = [];
            state.currentUserActivity = [];
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET USERS =====
            .addCase(getUsers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getUsers.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.users = responseData.data || [];
                state.pagination = responseData.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0,
                    stats: {
                        total: 0,
                        active: 0,
                        pending: 0,
                        inactive: 0,
                        blocked: 0
                    }
                };
                state.successMessage = payload.message || 'Users retrieved successfully';
                state.apiMessage = payload.message || 'Users retrieved successfully';
                state.error = null;
            })
            .addCase(getUsers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch users';
                state.apiMessage = action.payload?.message || 'Failed to fetch users';
                state.successMessage = null;
                state.users = [];
                state.pagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0,
                    stats: {
                        total: 0,
                        active: 0,
                        pending: 0,
                        inactive: 0,
                        blocked: 0
                    }
                };
            })

            // ===== GET USER =====
            .addCase(getUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getUser.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentUser = payload.user || null;
                state.currentUserPermissions = payload.permissions || [];
                state.currentUserActivity = payload.activityLog || [];
                state.successMessage = payload.message || 'User retrieved successfully';
                state.apiMessage = payload.message || 'User retrieved successfully';
                state.error = null;
            })
            .addCase(getUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch user';
                state.apiMessage = action.payload?.message || 'Failed to fetch user';
                state.successMessage = null;
                state.currentUser = null;
                state.currentUserPermissions = [];
                state.currentUserActivity = [];
            })

            // ===== CREATE USER =====
            .addCase(createUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createUser.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'User created successfully';
                state.apiMessage = payload.message || 'User created successfully';
                state.error = null;
                if (payload.user) {
                    state.users = [payload.user, ...state.users];
                    state.pagination.total += 1;
                }
            })
            .addCase(createUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create user';
                state.apiMessage = action.payload?.message || 'Failed to create user';
                state.successMessage = null;
            })

            // ===== UPDATE USER =====
            .addCase(updateUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'User updated successfully';
                state.apiMessage = payload.message || 'User updated successfully';
                state.error = null;
                if (payload.user) {
                    state.users = state.users.map(user => 
                        user.id === payload.user.id ? payload.user : user
                    );
                    if (state.currentUser?.id === payload.user.id) {
                        state.currentUser = payload.user;
                    }
                }
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update user';
                state.apiMessage = action.payload?.message || 'Failed to update user';
                state.successMessage = null;
            })

            // ===== DELETE USER =====
            .addCase(deleteUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'User deleted successfully';
                state.apiMessage = payload.message || 'User deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.users = state.users.filter(user => user.id !== payload.id);
                    state.pagination.total -= 1;
                    if (state.currentUser?.id === payload.id) {
                        state.currentUser = null;
                        state.currentUserPermissions = [];
                        state.currentUserActivity = [];
                    }
                }
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete user';
                state.apiMessage = action.payload?.message || 'Failed to delete user';
                state.successMessage = null;
            })

            // ===== APPROVE USER =====
            .addCase(approveUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(approveUser.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'User approved successfully';
                state.apiMessage = payload.message || 'User approved successfully';
                state.error = null;
                if (payload.user) {
                    state.users = state.users.map(user => 
                        user.id === payload.user.id ? payload.user : user
                    );
                    if (state.currentUser?.id === payload.user.id) {
                        state.currentUser = payload.user;
                    }
                }
            })
            .addCase(approveUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to approve user';
                state.apiMessage = action.payload?.message || 'Failed to approve user';
                state.successMessage = null;
            })

            // ===== REJECT USER =====
            .addCase(rejectUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(rejectUser.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'User rejected successfully';
                state.apiMessage = payload.message || 'User rejected successfully';
                state.error = null;
                if (payload.user) {
                    state.users = state.users.map(user => 
                        user.id === payload.user.id ? payload.user : user
                    );
                    if (state.currentUser?.id === payload.user.id) {
                        state.currentUser = payload.user;
                    }
                }
            })
            .addCase(rejectUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to reject user';
                state.apiMessage = action.payload?.message || 'Failed to reject user';
                state.successMessage = null;
            })

            // ===== ACTIVATE USER =====
            .addCase(activateUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(activateUser.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'User activated successfully';
                state.apiMessage = payload.message || 'User activated successfully';
                state.error = null;
                if (payload.user) {
                    state.users = state.users.map(user => 
                        user.id === payload.user.id ? payload.user : user
                    );
                    if (state.currentUser?.id === payload.user.id) {
                        state.currentUser = payload.user;
                    }
                }
            })
            .addCase(activateUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to activate user';
                state.apiMessage = action.payload?.message || 'Failed to activate user';
                state.successMessage = null;
            })

            // ===== DEACTIVATE USER =====
            .addCase(deactivateUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deactivateUser.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'User deactivated successfully';
                state.apiMessage = payload.message || 'User deactivated successfully';
                state.error = null;
                if (payload.user) {
                    state.users = state.users.map(user => 
                        user.id === payload.user.id ? payload.user : user
                    );
                    if (state.currentUser?.id === payload.user.id) {
                        state.currentUser = payload.user;
                    }
                }
            })
            .addCase(deactivateUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to deactivate user';
                state.apiMessage = action.payload?.message || 'Failed to deactivate user';
                state.successMessage = null;
            })

            // ===== BLOCK USER =====
            .addCase(blockUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(blockUser.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'User blocked successfully';
                state.apiMessage = payload.message || 'User blocked successfully';
                state.error = null;
                if (payload.user) {
                    state.users = state.users.map(user => 
                        user.id === payload.user.id ? payload.user : user
                    );
                    if (state.currentUser?.id === payload.user.id) {
                        state.currentUser = payload.user;
                    }
                }
            })
            .addCase(blockUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to block user';
                state.apiMessage = action.payload?.message || 'Failed to block user';
                state.successMessage = null;
            })

            // ===== UNBLOCK USER =====
            .addCase(unblockUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(unblockUser.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'User unblocked successfully';
                state.apiMessage = payload.message || 'User unblocked successfully';
                state.error = null;
                if (payload.user) {
                    state.users = state.users.map(user => 
                        user.id === payload.user.id ? payload.user : user
                    );
                    if (state.currentUser?.id === payload.user.id) {
                        state.currentUser = payload.user;
                    }
                }
            })
            .addCase(unblockUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to unblock user';
                state.apiMessage = action.payload?.message || 'Failed to unblock user';
                state.successMessage = null;
            })

            // ===== BULK ACTION =====
            .addCase(bulkActionUsers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(bulkActionUsers.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Bulk action completed successfully';
                state.apiMessage = payload.message || 'Bulk action completed successfully';
                state.error = null;
                // Refresh users list
                // The actual refresh will happen when getUsers is called again
            })
            .addCase(bulkActionUsers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to perform bulk action';
                state.apiMessage = action.payload?.message || 'Failed to perform bulk action';
                state.successMessage = null;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearUserState,
} = userSlice.actions;

export const selectUsers = (state) => state.users?.users || [];
export const selectUserPagination = (state) => state.users?.pagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
    stats: {
        total: 0,
        active: 0,
        pending: 0,
        inactive: 0,
        blocked: 0
    }
};
export const selectCurrentUser = (state) => state.users?.currentUser || null;
export const selectCurrentUserPermissions = (state) => state.users?.currentUserPermissions || [];
export const selectCurrentUserActivity = (state) => state.users?.currentUserActivity || [];
export const selectUsersLoading = (state) => state.users?.isLoading || false;
export const selectUsersError = (state) => state.users?.error || null;
export const selectUsersSuccess = (state) => state.users?.successMessage || null;
export const selectUsersApiMessage = (state) => state.users?.apiMessage || null;

export default userSlice.reducer;